import type { SavedLogView } from '@central/api';
import { PageHeader, logRetention, type LogLevel, type LogRange } from '@central/ui';
import { Button, Select } from '@mantine/core';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { CustomerSelect } from '../components/CustomerSelect';
import { LogSearch, type LogFilters } from '../components/LogSearch';
import { SavedLogViewsModal } from '../components/SavedLogViews';
import { environmentOptions, useCustomerEnvironments } from '../lib/environment';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';

export interface LogsSearch {
  customerId?: string;
  environmentId?: string;
  /** A fixed window to start from, as a crash report links it. */
  start?: string;
  end?: string;
}

/** The starting filters a link names: its window, when both ends parse. */
function linkedFilters(s: LogsSearch): Partial<LogFilters> {
  const f: Partial<LogFilters> = {};
  if (s.start && s.end && Date.parse(s.start) < Date.parse(s.end)) {
    f.range = 'window';
    f.window = { start: s.start, end: s.end };
  }
  return f;
}

/** A saved view's filters. */
const viewFilters = (v: SavedLogView): Partial<LogFilters> => ({
  service: v.service ?? '',
  level: (v.level ?? '') as '' | LogLevel,
  range: v.range as LogRange,
  contains: v.contains ?? '',
});

/**
 * Staff log search (docs/design/AdminLogs.dc.html): one environment at a
 * time, chosen by customer then environment. The Loki tenant is the
 * environment's; every search is audited by the server. A link that names
 * an environment searches it at once; so does applying a saved view.
 */
export function Logs() {
  const navigate = useNavigate({ from: '/logs' });
  const search = useSearch({ from: '/app/logs' });
  const customerId = search.customerId ?? '';
  const environmentId = search.environmentId ?? '';
  const environments = useCustomerEnvironments(customerId);
  // A new preset remounts the search with its filters.
  const [preset, setPreset] = useState(() => ({ n: 0, filters: linkedFilters(search) }));
  const [current, setCurrent] = useState<LogFilters>();
  const [viewsOpen, setViewsOpen] = useState(false);

  const pick = (next: LogsSearch) => void navigate({ search: next, replace: true });
  const apply = (v: SavedLogView) => {
    setViewsOpen(false);
    void navigate({
      search: { customerId: v.customerId, environmentId: v.environmentId },
      replace: true,
    }).then(() => setPreset((p) => ({ n: p.n + 1, filters: viewFilters(v) })));
  };

  return (
    <>
      <PageHeader
        title="Logs"
        subtitle="Every enrolled site · each customer is its own log tenant · access is written to the audit log"
        actions={
          <Button variant="default" onClick={() => setViewsOpen(true)}>
            Saved views
          </Button>
        }
      />
      <SavedLogViewsModal
        opened={viewsOpen}
        onClose={() => setViewsOpen(false)}
        current={current && { customerId, environmentId, filters: current }}
        onApply={apply}
      />
      <LogSearch
        key={preset.n}
        environmentId={environmentId}
        searchAtOnce={!!environmentId}
        initial={preset.filters}
        onChange={setCurrent}
        idleHint={
          environmentId
            ? 'Choose filters and search.'
            : 'Choose a customer and an environment to search its logs.'
        }
        leading={
          <>
            <CustomerSelect
              label="Customer"
              placeholder="Choose a customer"
              value={customerId}
              onChange={(id) => pick({ customerId: id || undefined })}
            />
            <Select
              label="Environment"
              placeholder={customerId ? 'Choose an environment' : 'Choose a customer first'}
              disabled={!customerId}
              data={environmentOptions(environments.data)}
              value={environmentId || null}
              onChange={(v) => pick({ customerId, environmentId: v ?? undefined })}
              error={environments.error ? errorMessage(environments.error) : undefined}
            />
          </>
        }
      />
      <p style={{ margin: 0, fontSize: 12, ...muted }}>{logRetention}</p>
    </>
  );
}
